// IndexedDB를 사용한 파일 저장 (대용량 파일 지원)

const DB_NAME = 'VideoEditorDB'
const DB_VERSION = 1
const STORE_NAME = 'projectFiles'

// IndexedDB 초기화
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    
    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result
      
      // 기존 스토어가 있으면 삭제
      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME)
      }
      
      // 새 스토어 생성
      db.createObjectStore(STORE_NAME, { keyPath: 'id' })
    }
  })
}

// 파일 저장
export const saveFileToIndexedDB = async (projectId, sectionId, fileType, file) => {
  if (!file) return null
  
  try {
    const db = await initDB()
    const transaction = db.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    
    const id = `${projectId}_${sectionId}_${fileType}`
    
    let fileBlob, fileName, fileType2
    
    if (file instanceof File) {
      fileBlob = file
      fileName = file.name
      fileType2 = file.type
    } else if (file instanceof Blob) {
      fileBlob = file
      fileName = `${fileType}.${fileType === 'mp4' ? 'mp4' : fileType === 'audio' ? 'webm' : 'dat'}`
      fileType2 = file.type || (fileType === 'mp4' ? 'video/mp4' : fileType === 'audio' ? 'audio/webm' : 'application/octet-stream')
    } else if (typeof file === 'string' && file.startsWith('blob:')) {
      const response = await fetch(file)
      fileBlob = await response.blob()
      fileName = `${fileType}.${fileType === 'mp4' ? 'mp4' : fileType === 'audio' ? 'webm' : 'dat'}`
      fileType2 = fileBlob.type || (fileType === 'mp4' ? 'video/mp4' : fileType === 'audio' ? 'audio/webm' : 'application/octet-stream')
    } else {
      console.error('Invalid file type for IndexedDB storage:', file)
      return null
    }
    
    const fileData = {
      id,
      projectId,
      sectionId,
      fileType,
      file: fileBlob,
      name: fileName,
      type: fileType2,
      timestamp: Date.now()
    }
    
    await new Promise((resolve, reject) => {
      const request = store.put(fileData)
      request.onsuccess = resolve
      request.onerror = () => reject(request.error)
    })
    
    return id
  } catch (error) {
    console.error('IndexedDB save error:', error)
    return null
  }
}

// 파일 로드
export const loadFileFromIndexedDB = async (fileId) => {
  if (!fileId) return null
  
  try {
    const db = await initDB()
    const transaction = db.transaction([STORE_NAME], 'readonly')
    const store = transaction.objectStore(STORE_NAME)
    
    return new Promise((resolve, reject) => {
      const request = store.get(fileId)
      
      request.onsuccess = () => {
        const result = request.result
        if (result && result.file) {
          // Blob을 File 객체로 변환
          const file = new File([result.file], result.name, { type: result.type })
          resolve(file)
        } else {
          resolve(null)
        }
      }
      
      request.onerror = () => reject(request.error)
    })
  } catch (error) {
    console.error('IndexedDB load error:', error)
    return null
  }
}

// 프로젝트의 모든 파일 삭제
export const deleteProjectFiles = async (projectId) => {
  try {
    const db = await initDB()
    const transaction = db.transaction([STORE_NAME], 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    
    // 모든 레코드를 가져와서 projectId가 일치하는 것만 삭제
    const request = store.getAll()
    
    return new Promise((resolve, reject) => {
      request.onsuccess = async () => {
        const allRecords = request.result
        const projectRecords = allRecords.filter(r => r.projectId === projectId)
        
        for (const record of projectRecords) {
          await store.delete(record.id)
        }
        
        resolve(projectRecords.length)
      }
      
      request.onerror = () => reject(request.error)
    })
  } catch (error) {
    console.error('IndexedDB delete error:', error)
    return 0
  }
}

// 섹션 데이터를 IndexedDB용으로 준비
export const prepareSectionsForIndexedDB = async (projectId, sections) => {
  const preparedSections = []
  
  for (const section of sections) {
    const preparedSection = { ...section }
    
    // imageUrl과 videoUrl은 항상 유지 (백엔드가 필요로 함)
    // 이미 문자열 URL이면 그대로 유지
    
    // 이미지 파일 저장
    if (section.imageFile instanceof File) {
      const fileId = await saveFileToIndexedDB(projectId, section.id, 'image', section.imageFile)
      if (fileId) {
        preparedSection.imageFileId = fileId
      }
      delete preparedSection.imageFile  // File 객체만 삭제
    }
    
    // imageUrl과 videoUrl은 유지 (백엔드가 필요로 함)
    // 이미 문자열 URL이면 그대로 유지
    
    // 비디오 파일 저장
    if (section.videoFile instanceof File) {
      // 비디오 파일이 적절한 MIME 타입을 가지도록 보장
      let videoFile = section.videoFile
      if (!videoFile.type || !videoFile.type.startsWith('video/')) {
        // MIME 타입이 없는 경우 확장자로 판단
        const ext = videoFile.name.split('.').pop()?.toLowerCase()
        const mimeType = ext === 'webm' ? 'video/webm' : 'video/mp4'
        videoFile = new File([videoFile], videoFile.name, { type: mimeType })
      }
      
      const fileId = await saveFileToIndexedDB(projectId, section.id, 'video', videoFile)
      if (fileId) {
        preparedSection.videoFileId = fileId
      }
      delete preparedSection.videoFile  // File 객체만 삭제
    }
    
    // MP4 Blob URL 저장
    if (section.mp4File && section.mp4File.startsWith('blob:')) {
      const blob = await fetch(section.mp4File).then(r => r.blob())
      // Blob이 video/mp4 타입을 가지도록 보장
      const mp4Blob = blob.type === 'video/mp4' ? blob : new Blob([blob], { type: 'video/mp4' })
      const fileId = await saveFileToIndexedDB(projectId, section.id, 'mp4', mp4Blob)
      if (fileId) {
        preparedSection.mp4FileId = fileId
      }
      delete preparedSection.mp4File
    }
    
    // 오디오 처리
    if (section.audioFile) {
      if (section.audioFile instanceof File) {
        // File 객체인 경우 IndexedDB에 저장
        const fileId = await saveFileToIndexedDB(projectId, section.id, 'audio', section.audioFile)
        if (fileId) {
          preparedSection.audioFileId = fileId
        }
        delete preparedSection.audioFile  // File 객체만 삭제
      } else if (typeof section.audioFile === 'string' && section.audioFile.startsWith('blob:')) {
        // Blob URL인 경우 IndexedDB에 저장
        const blob = await fetch(section.audioFile).then(r => r.blob())
        const fileId = await saveFileToIndexedDB(projectId, section.id, 'audio', blob)
        if (fileId) {
          preparedSection.audioFileId = fileId
        }
        delete preparedSection.audioFile  // Blob URL 삭제
      }
      // Supabase URL (https://)인 경우는 그대로 유지
    }
    
    preparedSections.push(preparedSection)
  }
  
  return preparedSections
}

// IndexedDB에서 섹션 데이터 복원
export const restoreSectionsFromIndexedDB = async (savedSections) => {
  if (!savedSections) return []
  
  const restoredSections = []
  
  for (const section of savedSections) {
    const restoredSection = { ...section }
    
    // 이미지 파일 복원
    if (section.imageFileId) {
      const file = await loadFileFromIndexedDB(section.imageFileId)
      if (file) {
        restoredSection.imageFile = file
        // console.log('Restored image from IndexedDB:', file.name, file.size)
      }
      delete restoredSection.imageFileId
    }
    
    // 비디오 파일 복원
    if (section.videoFileId) {
      const file = await loadFileFromIndexedDB(section.videoFileId)
      if (file) {
        // File 객체가 비디오 타입인지 확인
        let videoFile = file
        if (!file.type || !file.type.startsWith('video/')) {
          // MIME 타입이 없거나 비디오가 아닌 경우 mp4로 재생성
          videoFile = new File([file], file.name || 'video.mp4', { type: 'video/mp4' })
        }
        
        restoredSection.videoFile = videoFile
        // 비디오 파일의 Blob URL도 생성
        restoredSection.mp4File = URL.createObjectURL(videoFile)
        // console.log('Restored video from IndexedDB:', videoFile.name, videoFile.size, videoFile.type)
      }
      delete restoredSection.videoFileId
    }
    
    // MP4 Blob URL 복원
    if (section.mp4FileId) {
      const file = await loadFileFromIndexedDB(section.mp4FileId)
      if (file) {
        // MP4 파일이 적절한 MIME 타입을 가지도록 보장
        let mp4File = file
        if (!file.type || file.type !== 'video/mp4') {
          mp4File = new File([file], file.name || 'video.mp4', { type: 'video/mp4' })
        }
        restoredSection.mp4File = URL.createObjectURL(mp4File)
        // console.log('Restored MP4 from IndexedDB:', mp4File.type, mp4File.size)
      }
      delete restoredSection.mp4FileId
    }
    
    // 오디오 Blob URL 복원
    if (section.audioFileId) {
      const file = await loadFileFromIndexedDB(section.audioFileId)
      if (file) {
        restoredSection.audioFile = URL.createObjectURL(file)
        // console.log('Restored audio from IndexedDB')
      }
      delete restoredSection.audioFileId
    }
    
    restoredSections.push(restoredSection)
  }
  
  return restoredSections
}